import { useCallback, useRef } from 'react';
import type { ECharts } from 'echarts';

import { AXIS_LABEL_FONT_SIZE, hasOverlappingAxisLabels } from 'utils/chart-options/shared/axis';
import { measureTextWidth } from 'utils/chart-options/shared/measureText';

type AutoRotateCategoryAxisLabelsOptions = {
  labels: string[];
  fontFamily: string;
  xAxisIndex?: number;
};

type AxisLabelRotationConfig = Required<AutoRotateCategoryAxisLabelsOptions>;

const getVisibleLabelBounds = (chart: ECharts, { labels, fontFamily, xAxisIndex }: AxisLabelRotationConfig) => {
  const chartWidth = chart.getWidth();
  const font = `${AXIS_LABEL_FONT_SIZE}px ${fontFamily}`;

  return labels.flatMap((label, index) => {
    const center = chart.convertToPixel({ xAxisIndex }, index);
    if (typeof center !== 'number' || center < 0 || center > chartWidth) return [];

    return [{ center, width: measureTextWidth(label, font) }];
  });
};

// Uses ECharts' public coordinate API after layout. `finished` also fires after dataZoom and
// EChartsBase-owned resizes, so the rotation follows the labels currently visible in the chart.
//
// This is one of the two documented raw-instance routes (the other is EChartsBaseHandle.withChart):
// it needs setOption / getWidth / on, which the readiness-safe handle deliberately does not expose,
// and onChartReady only ever hands it a chart that has finished initializing. It does not remove
// its `finished` listener: the listener's lifetime is the instance's — echarts-for-react disposes
// the instance on re-init, which drops every handler with it — and the hook returns a bare callback
// with no unmount hook from which to detach. The isDisposed() guard below covers the one remaining
// window, a `finished` callback already queued when dispose lands.
export const useAutoRotateCategoryAxisLabels = ({
  labels,
  fontFamily,
  xAxisIndex = 0,
}: AutoRotateCategoryAxisLabelsOptions): ((chart: ECharts) => void) => {
  const configRef = useRef<AxisLabelRotationConfig>({ labels, fontFamily, xAxisIndex });
  const lastRotationRef = useRef<boolean | null>(null);
  const attachedChartsRef = useRef<WeakSet<ECharts>>(new WeakSet());
  configRef.current = { labels, fontFamily, xAxisIndex };

  return useCallback((chart: ECharts): void => {
    if (attachedChartsRef.current.has(chart)) return;
    attachedChartsRef.current.add(chart);
    lastRotationRef.current = null;

    const updateAxisLabelRotation = (): void => {
      if (chart.isDisposed()) return;
      const shouldRotate = hasOverlappingAxisLabels(getVisibleLabelBounds(chart, configRef.current));
      if (lastRotationRef.current === shouldRotate) return;

      lastRotationRef.current = shouldRotate;
      chart.setOption({
        xAxis: {
          axisLabel: {
            rotate: shouldRotate ? 45 : 0,
            align: shouldRotate ? 'right' : 'center',
          },
        },
      });
    };

    chart.on('finished', updateAxisLabelRotation);
    updateAxisLabelRotation();
  }, []);
};
